// src/component/legacymagnet/PersonalizeModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { CloseIcon, DeleteICon } from '../../public/icons';
import reviewService from '../../services/reviewService';

const STEP_DRAFT_KEY = 'kapta_legacy_step_personalize';

const loadStepDraft = () => {
  try {
    const raw = localStorage.getItem(STEP_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.error('Failed to load personalize step draft:', err);
    return null;
  }
};

const saveStepDraft = (data) => {
  try {
    localStorage.setItem(STEP_DRAFT_KEY, JSON.stringify(data));
  } catch (err) {
    console.error('Failed to save personalize step draft:', err);
  }
};

const clearStepDraft = () => {
  try {
    localStorage.removeItem(STEP_DRAFT_KEY);
  } catch (err) {
    console.error('Failed to clear personalize step draft:', err);
  }
};

const PersonalizeModal = ({
  isOpen,
  onClose,
  onSave,
  onProgressChange,
  customizationData = {},
  isAdding = false
}) => {
  const [memoryTitle, setMemoryTitle] = useState('');
  const [memoryStory, setMemoryStory] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);
  const [allMagnets, setAllMagnets] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const draftHydratedRef = useRef(false);

  /* ───────── HYDRATE on open ───────── */
  useEffect(() => {
    if (!isOpen) return;

    const draft = loadStepDraft();
    if (!draft) {
      // First ever open — reset everything to blank
      setMemoryTitle('');
      setMemoryStory('');
      setUploadedImages([]);
      setUploadError('');
      setEditingIndex(null);
      setAllMagnets([]);
      draftHydratedRef.current = true;
      return;
    }

    if (typeof draft.memoryTitle === 'string') setMemoryTitle(draft.memoryTitle);
    if (typeof draft.memoryStory === 'string') setMemoryStory(draft.memoryStory);
    if (Array.isArray(draft.uploadedImages)) setUploadedImages(draft.uploadedImages);
    if (Array.isArray(draft.allMagnets)) setAllMagnets(draft.allMagnets);
    if (draft.editingIndex === null || typeof draft.editingIndex === 'number') {
      setEditingIndex(draft.editingIndex);
    }

    draftHydratedRef.current = true;
  }, [isOpen]);

  /* ───────── AUTOSAVE + broadcast on every change ───────── */
  useEffect(() => {
    if (!isOpen) return;
    if (!draftHydratedRef.current) return;

    const draft = {
      memoryTitle,
      memoryStory,
      uploadedImages,
      allMagnets,
      editingIndex
    };

    saveStepDraft(draft);
    onProgressChange?.(draft);
  }, [
    isOpen,
    memoryTitle,
    memoryStory,
    uploadedImages,
    allMagnets,
    editingIndex,
    onProgressChange
  ]);

  if (!isOpen) return null;

  const expectedCount = 16;

  /* ───────── Upload to Cloudinary ───────── */
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadError('');
    setIsUploading(true);

    try {
      const uploaded = [];
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        if (file.size > 10 * 1024 * 1024) {
          setUploadError(`${file.name} exceeds 10MB and was skipped.`);
          continue;
        }
        const result = await reviewService.uploadImageToCloudinary(file);
        uploaded.push({
          url: result.url,
          publicId: result.publicId,
          name: file.name
        });
      }
      setUploadedImages((prev) => [...prev, ...uploaded]);
    } catch (err) {
      console.error('Image upload failed:', err);
      setUploadError('Failed to upload one or more images. Please try again.');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveImage = (index) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setMemoryTitle('');
    setMemoryStory('');
    setUploadedImages([]);
    setUploadError('');
    setEditingIndex(null);
  };

  const hasCurrentContent =
    memoryTitle.trim() !== '' ||
    memoryStory.trim() !== '' ||
    uploadedImages.length > 0;

  const handleAddAnother = () => {
    if (!hasCurrentContent) return;

    const magnet = {
      memoryTitle: memoryTitle.trim(),
      memoryStory: memoryStory.trim(),
      uploadedImages: [...uploadedImages]
    };

    setAllMagnets((prev) => {
      if (editingIndex !== null) {
        const next = [...prev];
        next[editingIndex] = magnet;
        return next;
      }
      return [...prev, magnet];
    });

    resetForm();
  };

  const handleEditSavedMagnet = (index) => {
    const magnet = allMagnets[index];
    setMemoryTitle(magnet.memoryTitle);
    setMemoryStory(magnet.memoryStory);
    setUploadedImages(magnet.uploadedImages);
    setEditingIndex(index);

    document
      .getElementById('photo-upload-section')
      ?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDeleteSavedMagnet = (index) => {
    setAllMagnets((prev) => prev.filter((_, i) => i !== index));

    if (editingIndex === index) {
      resetForm();
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  };

  /* ───────── Final save ───────── */
  const handleFinalSave = () => {
    let finalMagnets = [...allMagnets];

    if (hasCurrentContent) {
      const current = {
        memoryTitle: memoryTitle.trim(),
        memoryStory: memoryStory.trim(),
        uploadedImages: [...uploadedImages]
      };

      if (editingIndex !== null) {
        finalMagnets[editingIndex] = current;
      } else {
        finalMagnets.push(current);
      }
    }

    if (finalMagnets.length === 0) return;

    const finalData = {
      ...(typeof customizationData === 'object' ? customizationData : {}),
      magnets: finalMagnets
    };

    /* 🟢 Clear the modal's local draft — the parent now owns it */
    clearStepDraft();

    onSave(finalData);
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300"
        onClick={onClose}
      />

      <div className="fixed right-0 top-0 h-full w-full sm:w-[90%] md:w-[70%] lg:w-[60%] xl:w-[50%] 2xl:w-[40%] bg-[#F9F6F2] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mx-4 sm:mx-6 mb-4 mt-4 sm:mt-6 pb-3 sm:pb-4 border-b-2 border-[#DBDBDB]">
            <div className="text-left">
              <h2 className="text-xl sm:text-2xl font-bold text-[#1C2A46]">
                Customise Your Legacy Magnets
              </h2>
              <p className="text-[#6B635D] text-xs sm:text-sm mt-1">
                Add {expectedCount} photos and stories behind each of the
                magnets.
                {allMagnets.length > 0 && (
                  <span className="ml-2 font-medium">
                    ({allMagnets.length}/{expectedCount} completed)
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] rounded self-end sm:self-auto"
            >
              <CloseIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[#1C2A46]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {allMagnets.length > 0 && (
              <div className="mb-6 sm:mb-8">
                <div className="space-y-4 sm:space-y-6">
                  {allMagnets.map((magnet, index) => {
                    const isBeingEdited = editingIndex === index;

                    return (
                      <div
                        key={index}
                        className={`flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-[#DBDBDB] last:border-0 ${
                          isBeingEdited ? 'bg-[#FFF4D6] p-3 rounded' : ''
                        }`}
                      >
                        <div className="overflow-hidden flex-shrink-0 self-center sm:self-auto">
                          {magnet.uploadedImages.length > 0 ? (
                            <img
                              src={magnet.uploadedImages[0].url}
                              alt={`Magnet ${index + 1}`}
                              className="h-24 w-24 sm:h-28 sm:w-28 md:h-32 md:w-32 object-cover rounded"
                            />
                          ) : (
                            <div className="h-24 w-24 sm:h-28 sm:w-28 md:h-32 md:w-32 bg-black flex flex-col justify-center items-center rounded">
                              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-white rounded-full flex items-center justify-center">
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M12 5v14M5 12h14"
                                    stroke="#1C2A46"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-1 flex-col sm:flex-row gap-3 sm:gap-4">
                          <div className="text-left flex-1 min-w-0">
                            <h3 className="text-base sm:text-lg md:text-xl font-bold text-[#1C2A46] mb-0.5 break-words">
                              {magnet.memoryTitle || 'Untitled Magnet'}
                            </h3>
                            <p className="text-[#6B635D] text-xs sm:text-sm leading-relaxed line-clamp-3">
                              {magnet.memoryStory || 'No story added yet'}
                            </p>
                          </div>

                          <div className="flex gap-3 sm:gap-4 self-start sm:self-center flex-shrink-0">
                            {isBeingEdited ? (
                              <span className="text-xs sm:text-sm text-[#8F7F2F] font-medium">
                                Editing…
                              </span>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleEditSavedMagnet(index)}
                                  disabled={isUploading}
                                  className="text-[#1C2A46] underline font-medium text-sm sm:text-base hover:text-[#C49660] transition-colors disabled:opacity-50"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteSavedMagnet(index)}
                                  disabled={isUploading}
                                  className="text-red-600 underline font-medium text-sm sm:text-base hover:text-red-700 transition-colors disabled:opacity-50"
                                >
                                  Remove
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {editingIndex !== null && (
              <div className="mb-5 bg-[#FFF4D6] border-l-4 border-[#D8A100] px-4 py-3 rounded-r">
                <p className="text-sm text-[#8F7F2F] text-left">
                  ✏️ Editing magnet {editingIndex + 1}. Make your changes and
                  click <strong>Save changes</strong> to update it.
                </p>
              </div>
            )}

            <div
              className="flex flex-col sm:flex-row gap-5 w-full"
              id="photo-upload-section"
            >
              <div className="mb-4 sm:mb-6 w-full sm:w-1/2 md:w-1/3">
                <h4 className="text-sm sm:text-base font-normal text-[#1C2A46] mb-2 sm:mb-3 text-left">
                  Add Photo
                </h4>
                <div
                  className={`bg-black h-56 sm:h-64 w-full sm:w-48 md:w-52 flex flex-col justify-center items-center rounded ${
                    isUploading
                      ? 'cursor-wait'
                      : 'cursor-pointer hover:opacity-90 transition-opacity'
                  }`}
                  onClick={() => {
                    if (!isUploading) {
                      document.getElementById('photo-upload').click();
                    }
                  }}
                >
                  {isUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
                      <p className="text-sm text-white mt-2">Uploading…</p>
                    </>
                  ) : uploadedImages.length > 0 ? (
                    <div className="relative w-full h-full">
                      <img
                        src={uploadedImages[0].url}
                        alt="Uploaded preview"
                        className="w-full h-full object-cover rounded"
                      />
                      <div
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center cursor-pointer hover:bg-red-600 text-xs sm:text-base"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage(0);
                        }}
                      >
                        ×
                      </div>
                      {uploadedImages.length > 1 && (
                        <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs rounded">
                          +{uploadedImages.length - 1} more
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full flex items-center justify-center">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 5v14M5 12h14"
                            stroke="#1C2A46"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                      <p className="text-xs sm:text-sm text-white mt-2 text-center px-2">
                        PNG, JPG, JPEG
                        <br />
                        (max size. 10mb)
                      </p>
                    </>
                  )}
                </div>

                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                  multiple
                  disabled={isUploading}
                />

                {uploadError && (
                  <p className="mt-2 text-xs text-red-600 text-left">
                    {uploadError}
                  </p>
                )}
              </div>

              <div className="w-full sm:w-2/3">
                <div className="mb-4">
                  <h4 className="text-sm sm:text-base font-normal text-[#1C2A46] mb-2 sm:mb-3 text-left">
                    Memory Title (optional)
                  </h4>
                  <input
                    type="text"
                    placeholder='e.g. "For When You Need a Reminder"'
                    value={memoryTitle}
                    onChange={(e) => setMemoryTitle(e.target.value)}
                    className="w-full px-3 sm:px-4 py-3 sm:py-4 border border-[#E4E4E4] bg-[#F5F5F5] text-[#6B635D] focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent placeholder-[#6B635D] placeholder:font-light text-sm sm:text-base"
                  />
                </div>

                <div>
                  <h4 className="text-sm sm:text-base font-normal text-[#1C2A46] mb-2 sm:mb-3 text-left">
                    Memory Story (Back of magnet)*
                  </h4>
                  <textarea
                    placeholder="Tell the story behind this moment. who was there? what happened? and why does it matter?"
                    value={memoryStory}
                    onChange={(e) => {
                      if (e.target.value.length <= 2000) {
                        setMemoryStory(e.target.value);
                      }
                    }}
                    className="w-full px-3 sm:px-4 py-3 sm:py-4 border border-[#E4E4E4] bg-[#F5F5F5] text-[#6B635D] focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent placeholder-[#666666] placeholder:font-light h-32 sm:h-40 text-sm sm:text-base"
                    maxLength="2000"
                  />
                  <div className="text-right text-xs sm:text-sm text-gray-500 mt-1">
                    {memoryStory.length}/2000
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end items-start mt-4">
              <button
                onClick={handleAddAnother}
                disabled={isUploading || !hasCurrentContent}
                className={`w-full sm:w-40 border py-2.5 sm:py-3 font-medium transition-colors text-sm sm:text-base ${
                  isUploading || !hasCurrentContent
                    ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                    : 'border-[#DBDBDB] text-[#1C2A46] hover:border-[#D2A146]'
                }`}
              >
                {editingIndex !== null ? 'Save changes' : '+ Add another'}
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-6 border-t border-[#DBDBDB]">
            <button
              onClick={handleFinalSave}
              disabled={
                (allMagnets.length === 0 && !hasCurrentContent) ||
                isUploading ||
                isAdding
              }
              className={`w-full py-3 font-medium transition-colors text-sm sm:text-base flex items-center justify-center space-x-2 ${
                (allMagnets.length === 0 && !hasCurrentContent) ||
                isUploading ||
                isAdding
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[#D2A146] text-white hover:bg-[#C49660]'
              }`}
            >
              {isAdding ? (
                <>
                  <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Adding…</span>
                </>
              ) : isUploading ? (
                'Uploading…'
              ) : allMagnets.length === 0 && !hasCurrentContent ? (
                'Add at least one magnet'
              ) : (
                `Save ${
                  allMagnets.length + (hasCurrentContent ? 1 : 0)
                } Magnet${
                  allMagnets.length + (hasCurrentContent ? 1 : 0) !== 1
                    ? 's'
                    : ''
                }`
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PersonalizeModal;